import React, { useEffect, useState } from 'react';
import { User, AlertCircle } from 'lucide-react';
import { fetchCollection } from '../lib/api';
import { Helmet } from 'react-helmet-async';

export default function Team() {
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const loadStaff = async () => {
      try {
        // Try team_members first as it seems to be the primary one based on recent activity
        let data = await fetchCollection('team_members');
        
        // If empty, try staff
        if (!data || data.length === 0) {
          data = await fetchCollection('staff');
        }

        if (data && data.length > 0) {
          setStaff(data);
        } else {
          setError("Kunne ikke hente medarbejderdata.");
        }
      } catch (err) {
        console.error('Error loading staff:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    loadStaff();
  }, []);

  // Dynamically get all categories present in the data
  const availableCategories = [...new Set(staff.map(member => member.category?.da))].filter(Boolean);
  
  // Sort categories to have 'Læger' first, then 'Praksismanager', then others
  const sortedCategories = availableCategories.sort((a, b) => {
    if (a === 'Læger') return -1;
    if (b === 'Læger') return 1;
    if (a === 'Praksismanager') return -1;
    if (b === 'Praksismanager') return 1;
    return a.localeCompare(b);
  });

  const getStaffByCategory = (category) => {
    const members = staff.filter(member => {
      const name = member.name?.da || '';
      if (name.includes('Emilia') || name.includes('Julia Tryzna')) {
        return false;
      }
      return member.category?.da === category;
    });

    if (category === 'Læger') {
      return [...members].sort((a, b) => {
        const getName = (m) => m.name?.da || '';
        
        const getOrder = (name) => {
          if (name.includes('Hans Thorvald Stokkendal')) return 1;
          if (name.includes('Louise Bjørkholt')) return 2;
          if (name.includes('Søren Refsgaard')) return 3;
          if (name.includes('Niels Vandborg')) return 4;
          if (name.includes('Sune Hemmingsen') || name.includes('Simon')) return 5;
          if (name.includes('Anne Marie Werlinrud') || name.includes('Anne-Marie Werlinrud')) return 6;
          if (name.includes('Katrine Høgenhaven') || name.includes('Katrine')) return 7;
          if (name.includes('Martha Veber Fogh') || name.includes('Martha')) return 8;
          if (name.includes('Julie Theill Jensen') || name.includes('Julie')) return 9;
          return 100; // default for any other doctor
        };

        return getOrder(getName(a)) - getOrder(getName(b));
      });
    }
    if (category === 'Praksispersonale') {
      return [...members].sort((a, b) => {
        const getName = (m) => m.name?.da || '';
        
        const getOrder = (name) => {
          if (name.includes('Hatice')) return 1;
          if (name.includes('Mette')) return 2;
          if (name.includes('Rikke')) return 3;
          if (name.includes('Christina')) return 4;
          if (name.includes('Pernille')) return 5;
          if (name.includes('Linda')) return 6;
          if (name.includes('Karen')) return 7;
          if (name.includes('Sandy')) return 8;
          return 100;
        };

        return getOrder(getName(a)) - getOrder(getName(b));
      });
    }
    return members;
  };

  return (
    <main className="pt-32 pb-24">
      <Helmet>
        <title>Mød lægerne & personalet | Hellerup Lægehus</title>
        <meta name="description" content="Mød vores erfarne team af læger og personale hos Hellerup Lægehus. Vi står klar til at hjælpe dig med høj faglighed og omsorg. Læs mere her." />
        <meta property="og:title" content="Mød lægerne & personalet | Hellerup Lægehus" />
        <meta property="og:description" content="Mød vores erfarne team af læger og personale hos Hellerup Lægehus. Vi står klar til at hjælpe dig med høj faglighed og omsorg. Læs mere her." />
        <html lang="da-DK" />
      </Helmet>
      <div className="max-w-7xl mx-auto px-6">
        <div className="mb-20">
          <div className="flex items-center gap-4 mb-6">
            <div className="h-[1px] w-12 bg-[#4b717a]" />
            <span className="text-[#4b717a] text-xs tracking-[0.4em] uppercase font-bold">Vores Team</span>
          </div>
          <h1 className="text-5xl md:text-6xl font-sans font-medium text-black mb-8 leading-tight tracking-tight">Mød lægerne og <br />personalet</h1>
          <p className="text-slate-600 text-lg max-w-2xl leading-relaxed">
            Vi er et dedikeret hold af fagfolk, der arbejder sammen for at give dig den bedste behandling.
          </p>
        </div>


        {loading ? (
          <div className="flex justify-center py-24">
            <div className="animate-spin h-12 w-12 border-b-2 border-[#4b717a]"></div>
          </div>
        ) : error && staff.length === 0 ? (
          <div className="text-center py-24 bg-red-50 border border-dashed border-red-200">
            <AlertCircle className="mx-auto h-12 w-12 text-red-400 mb-4" />
            <p className="text-red-800 font-medium">{error}</p>
            <p className="text-red-600 text-sm mt-2">Prøv at genopfriske siden eller kontakt administratoren.</p>
          </div>
        ) : staff.length === 0 ? (
          <div className="text-center py-24 bg-slate-50 border border-dashed border-slate-200">
            <p className="text-slate-500">Ingen medarbejdere fundet i systemet.</p>
          </div>
        ) : (
          <div className="space-y-24">
            {sortedCategories.map(category => {
              const members = getStaffByCategory(category);
              if (members.length === 0) return null;

              return (
                <section key={category} className="space-y-12">
                  <div className="flex items-center gap-6">
                    <h2 className="text-3xl font-sans font-medium text-black shrink-0 leading-tight tracking-tight">{category}</h2>
                    <div className="h-[1px] w-full bg-slate-100" />
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
                    {members.map(member => (
                      <div key={member.id} className="group bg-white border border-slate-100 overflow-hidden hover:shadow-xl hover:shadow-slate-200/50 transition-all duration-300">
                        <div className="aspect-[4/5] bg-slate-50 relative overflow-hidden">
                          {member.image?.[0] ? (
                            <img 
                              src={member.image[0]} 
                              alt={member.name?.da || 'Medarbejder'}
                              className={`w-full h-full ${
                                member.name?.da?.includes('Søren') 
                                  ? 'object-contain' 
                                  : member.name?.da?.includes('Katrine')
                                  ? 'object-cover scale-[1.15] origin-[50%_45%] -translate-y-7'
                                  : member.name?.da?.includes('Martha')
                                  ? 'object-cover scale-[1.12] origin-[50%_40%] -translate-y-7'
                                  : 'object-cover'
                              } grayscale group-hover:grayscale-0 transition-all duration-500`}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-slate-300">
                              <User size={64} strokeWidth={1} />
                            </div>
                          )}
                        </div>
                        <div className="p-8">
                          <h3 className="text-xl font-sans font-medium text-black mb-1 leading-tight tracking-tight">{member.name?.da}</h3>
                          <p className="text-[#4b717a] font-medium text-sm mb-4">{member.role?.da}</p>
                          {member.description?.da && (
                            <div 
                              className="text-slate-600 text-sm leading-relaxed prose prose-slate prose-sm"
                              dangerouslySetInnerHTML={{ __html: member.description.da }}
                            />
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                </section>
              );
            })}
          </div>
        )}
      </div>
    </main>
  );
}
